/**
 * isiZulu: Settings
 *
 * What this file covers: Settings: profile, organisation, business hours, team and roles, notifications,
 * security, appearance and the other settings pages.
 *
 * Tone: Neutral and precise. Each setting says what changes when it is switched on.
 *
 * isiZulu: Everyday isiZulu as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const settings: Catalog = {
  'settings.nav.developer': 'Umthuthukisi',
  'settings.apiKeys.actions.create': 'Dala ukhiye we-API',
  'settings.apiKeys.columns.prefix': 'Isiqalo',
  'settings.apiKeys.empty': 'Abekho okhiye be-API okwamanje.',
  'settings.apiKeys.form.name': 'Igama lokhiye',
  'settings.apiKeys.loadError': 'Ayikwazanga ukulayisha okhiye be-API.',
  'settings.apiKeys.modal.revoke.title': 'Hoxisa ukhiye we-API?',
  'settings.apiKeys.toast.revoked': 'Ukhiye we-API uhoxisiwe.',
  'settings.appearance.comfortable': 'Kukhululekile',
  'settings.appearance.compact': 'Kuhlangene',
  'settings.appearance.cozy': 'Kufudumele',
  'settings.appearance.density': 'Ubuningi',
  'settings.appearance.theme': 'Itimu',
  'settings.badge.emailUnverified': 'I-imeyili ayiqinisekisiwe',
  'settings.badge.twoFactorOff': 'I-2FA ivaliwe',
  'settings.badge.twoFactorOn': 'I-2FA ivuliwe',
  'settings.cannedReplies.deleteFailed': 'Ayikwazanga ukususa.',
  'settings.cannedReplies.loadError': 'Ayikwazanga ukulayisha izimpendulo ezilungiselelwe.',
  'settings.nav.apiKeys': 'Okhiye be-API',
  'settings.nav.appearance': 'Ukubukeka',
  'settings.nav.notifications': 'Izaziso',
  'settings.nav.organisation': 'Inhlangano',
  'settings.nav.security': 'Ukuphepha',
  'settings.nav.team': 'Ithimba',
  'settings.nav.webhooks': 'Ama-webhook',
  'settings.notifications.actions.save': 'Londoloza okuthandayo',
  'settings.notifications.title': 'Izaziso nge-imeyili',
  'settings.notifications.toast.saved': 'Okuthandayo kwezaziso kulondoloziwe.',
  'settings.organisation.timezone': 'Isikhathi sendawo',
  'settings.organisation.toast.saved': 'Inhlangano ibuyekeziwe.',
  'settings.profile.toast.saved': 'Iphrofayela ibuyekeziwe.',
  'settings.security.actions.signOutAll': 'Phuma yonke indawo',
  'settings.security.activeSessions': 'Amaseshini asebenzayo',
  'settings.security.changePassword': 'Shintsha iphasiwedi',
  'settings.security.confirmNewPassword': 'Qinisekisa iphasiwedi entsha',
  'settings.security.currentPassword': 'Iphasiwedi yamanje',
  'settings.security.modal.signOutAll.title': 'Phuma yonke indawo?',
  'settings.security.toast.changed': 'Iphasiwedi ishintshiwe.',
  'settings.security.twoFactor.actions.disable': 'Khubaza i-2FA',
  'settings.security.twoFactor.actions.enable': 'Vumela i-2FA',
  'settings.security.twoFactor.actions.setup': 'Setha i-2FA',
  'settings.security.twoFactor.code': 'Ikhodi',
  'settings.security.twoFactor.title': 'Ukuqinisekiswa ngezigaba ezimbili',
  'settings.team.actions.invite': 'Mema ilungu leqembu',
  'settings.team.columns.joined': 'Kujoyine',
  'settings.team.form.tempPassword': 'Iphasiwedi yesikhashana',
  'settings.team.inviteFailed': 'Ukumema kwehlulekile',
  'settings.team.loadError': 'Ayikwazanga ukulayisha ithimba.',
  'settings.webhooks.actions.add': 'Engeza i-webhook',
  'settings.webhooks.columns.events': 'Imicimbi',
  'settings.webhooks.empty': 'Akukho ukubhalisela ama-webhook okwamanje.',
  'settings.webhooks.form.events': 'Izinhlobo zemicimbi',
  'settings.webhooks.form.url': 'I-URL yendawo yokuphela',
  'settings.webhooks.loadError': 'Ayikwazanga ukulayisha ama-webhook.',
  'settings.webhooks.modal.delete.title': 'Susa i-webhook?',
  'settings.webhooks.title': 'Ukubhalisela ama-webhook',
  'settings.webhooks.toast.deleted': 'I-webhook isusiwe.',
  'settings.webhooks.deliveries.response': 'Okuthunyelwe emuva ngumamukeli',
  'settings.webhooks.deliveries.showing': 'Kubonisa {{shown}} kwangu-{{total}}',
  'settings.webhooks.result.success': 'Kulethiwe',
  'settings.webhooks.result.blacklisted': 'Kwaliwe ngumamukeli',
  'settings.notifications.channel.email': 'I-imeyili',
  'settings.notifications.channel.whatsapp': 'I-WhatsApp',
  'settings.notifications.channel.webhook': 'I-webhook',
  'settings.notifications.channel.inApp': 'Ku-app',
  'settings.notifications.toggle.depleted.label': 'Ibhalansi iphelile',
  'settings.notifications.toggle.recovered.label': 'Ibhalansi ibuyile',
  'settings.notifications.toggle.dailySpend.label': 'Ukusetshenziswa kwansuku zonke',
  'settings.notifications.toggle.autoTopupSucceeded.label': 'Ukugcwaliswa okuzenzakalelayo kuphumelele',
  'settings.notifications.toggle.autoTopupFailed.label': 'Ukugcwaliswa okuzenzakalelayo kwehlulekile',
  'settings.apiKeys.seeDocs': 'Funda amabhukwana e-API agcwele',
  'settings.nav.contactFields': 'Izinkambu zoxhumana naye',
  'settings.hub.contactFields.hint': 'Imininingwane eyengeziwe oyigcina ngalowo oxhumana naye.',
  'settings.assistant.smallTalk.title': 'Izingxoxo ezincane',
  'settings.assistant.smallTalk.intro': 'Izimpendulo emilayezweni eyisibongo kuphela, ukuvuma noma ukuvalelisa, njengokuthi "ngiyabonga", "k" noma "sala kahle". Zithunyelwa ngokushesha, azikhokhelwa futhi azilokothi ziye endleleni yokungaqiniseki. Isilimi sokuxoxa namagama aseNingizimu Afrika (tnx, sharp, dankie, ngiyabonga) kuyaqondakala.',
  'settings.assistant.smallTalk.enabled': 'Phendula izingxoxo ezincane',
  'settings.assistant.smallTalk.thanks': 'Uma ikhasimende libonga',
  'settings.assistant.smallTalk.acknowledge': 'Uma ikhasimende lithi ok',
  'settings.assistant.smallTalk.goodbye': 'Uma ikhasimende livalelisa',
  'settings.assistant.smallTalk.hint': '{{contact.name ?? there}} iba igama lekhasimende. Shiya kungenalutho ukuze kusetshenziswe okuzenzakalelayo.',
  'settings.assistant.moderation.title': 'Ulimi olungamukeleki',
  'settings.assistant.moderation.intro': 'Izinhlamba, izithuko nezilimi zokucwasa ezivela kumakhasimende zithola impendulo eqinile, bese ingxoxo ifakwa ilebula nenothi yethimba lakho. I-ejenti ingavimba ikhasimende kuZilungiselelo zengxoxo.',
  'settings.assistant.moderation.enabled': 'Qapha ulimi olungamukeleki',
  'settings.assistant.moderation.action': 'Ngemva kokuphendula',
  'settings.assistant.moderation.actionHint': 'Ukudlulisela kumisa i-bot, ukuze ithimba lakho liqhubeke nengxoxo.',
  'settings.assistant.moderation.actions.warn': 'Qhubeka uphendula',
  'settings.assistant.moderation.actions.handover': 'Dlulisela ingxoxo ethimbeni',
  'settings.assistant.moderation.reply': 'Impendulo ekhasimendeni',
  'settings.assistant.moderation.replyHint': 'Shiya kungenalutho ukuze kusetshenziswe okuzenzakalelayo.',
  'settings.assistant.moderation.extra': 'Amagama okwengeza',
  'settings.assistant.moderation.extraHint': 'Igama elilodwa noma ibinzana emgqeni ngamunye. Kufaniswa amagama aphelele, kuhlanganise nezipelingi ezifihliwe njenge-f*ck.',
  'settings.assistant.moderation.allowed': 'Amagama okuvumela',
  'settings.assistant.moderation.allowedHint': 'Amagama ohlwini oluzenzakalelayo ibhizinisi lakho elingenankinga nawo, elilodwa emgqeni ngamunye.',
  'settings.assistant.moderation.showDefault': 'Buka uhlu oluzenzakalelayo (amagama angu-{{count}})',
  'settings.assistant.moderation.hideDefault': 'Fihla uhlu oluzenzakalelayo',
  'settings.assistant.pauseNotice.title': 'Uma ithimba lakho linengxoxo',
  'settings.assistant.pauseNotice.intro': 'Ikhasimende elibhala ngesikhathi umuntu enengxoxo litshelwa kanye ngosuku, ngezinkinobho zokulinda ithimba noma ukubuza umsizi. Akukho okuthunyelwayo uma ithimba lakho liphendule emizuzwini eyi-10 edlule.',
  'settings.assistant.pauseNotice.enabled': 'Tshela ikhasimende',
  'settings.assistant.pauseNotice.message': 'Umlayezo',
  'settings.assistant.pauseNotice.hint': 'Shiya kungenalutho ukuze kusetshenziswe okuzenzakalelayo.'
};

export default settings;
